import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grow,
  IconButton,
  Paper,
  Slide,
  Stack,
  TextField,
  useTheme,
} from "@mui/material";
import axios from "axios";
import React, { useCallback, useEffect, useRef, useState } from "react";
import Logo from "../../components/logo";
import { Body1, H6, Icone } from "../../components";
import { useToggle } from "react-use";
import ReCAPTCHA from "react-google-recaptcha";
import { API_KEY } from "../../config_api";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { toast } from "react-toastify";
import { AnimacaoCashback } from "../../components/sem-pesquisa";

const STR = {
  labelCompany: "Óticas Diniz - Cashback",
  labelBtnLogin: "Acessar",
  placeholderPassword: "Digite a sua senha",
  placeholderName: "Digite o seu email",
  labelFieldName: "EMAIL",
  labelFieldPassword: "SENHA",
  errorUsername: "* O email de usuário não foi digitado",
  errorPassword: "* A senha não foi digitada",
  internalError: "* Erro interno do servidor. Se persistir contacte o admin",
};
//
const validatorForm = yup.object().shape({
  email: yup.string().email().required(),
  senha: yup.string().min(1).required(),
});

const APP_DEBUG =
  window.location.hostname.search("localhost") !== -1 ||
  window.location.hostname.search(/^10\.5\.*/g) !== -1;

function Login() {
  // Para referenciar o captcha invisivel
  const recaptchaRef = useRef();
  const [ip, setIp] = useState(null);
  const colorPrimary = useTheme()?.palette?.primary?.main;
  const isMobile = useTheme()?.isMobile;

  // Recupera o ip atual
  useEffect(() => {
    (async function () {
      // Tenta obter o ip de origem, se conseguir o envia
      try {
        const resp = await axios.get("https://httpbin.org/ip");
        let ip = resp.data.origin ? resp.data.origin.split(",")[0].trim() : "";
        setIp(ip);
      } catch (err) {
        console.log(err);
      }
    })();
  }, []);

  return (
    <Stack direction={{ height: "100vh", xs: "column", md: "row" }} gap={1}>
      {colorPrimary && (
        <Box
          sx={{
            background: colorPrimary,
            minHeight: "100vh",
            width: "100vw",
            flex: 2,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <FormLogin recaptchaRef={recaptchaRef} ip={ip} />

          {APP_DEBUG ? null : (
            <ReCAPTCHA ref={recaptchaRef} size="invisible" sitekey={API_KEY} />
          )}
        </Box>
      )}
      {!isMobile && <ViewAnimate />}
    </Stack>
  );
}
//
const ViewAnimate = () => {
  return (
    <Box sx={{ flex: 1 }}>
      <AnimacaoCashback />
      <Slide direction="up" in mountOnEnter unmountOnExit>
        <Box>
          <H6 align="center">Cashback</H6>
          <Body1 align="center">
            Bem-vindo ao portal interno de acompanhamento de cashback. Acesse
            para gerenciar e monitorar os cashbacks enviados aos nossos
            clientes.
          </Body1>
        </Box>
      </Slide>
    </Box>
  );
};

//
const FormLogin = ({ recaptchaRef, ip }) => {
  const colorPrimary = useTheme()?.palette?.primary?.main;
  const [wait, setWait] = useToggle();
  const [viewPassword, setViewPassword] = useToggle();
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validatorForm),
  });
  //
  const sxPaper = {
    p: 1,
    pb: 2,
    background: "white",
    color: "black",
    width: {
      xs: "calc(100vw - 32px)",
      md: "60%",
    },
  };
  //
  const onSubmit = useCallback(
    async (val) => {
      const { email, senha } = val;
      setWait();
      const formData = {
        captcha: "X",
      };
      //
      if (!APP_DEBUG) {
        await recaptchaRef.current.reset();
        // Pega o token do recaptcha
        let TOKEN = recaptchaRef.current.getValue();
        TOKEN = await recaptchaRef.current.executeAsync();
        formData.captcha = TOKEN;
      }
      // Preenchendo os campos necessarios para login
      formData.email = email;
      formData.password = senha;
      // if (ip) formData.ip = ip;
      try {
        await axios.post("/", formData);
        // Tudo certo redirecione o usuario
        window.location.href = "/home";
      } catch (err) {
        if (err?.response?.data?.message) {
          toast.dark(err.response.data.message, { type: "error" });
          return false;
        }
        toast.dark(STR.internalError, { type: "error" });
        return false;
      } finally {
        setWait();
      }
    },
    [recaptchaRef, setWait]
  );
  //
  const schemaFormFields = [
    {
      name: "email",
      label: STR.labelFieldName,
      type: "text",
      placeholder: STR.placeholderName,
      prefixIcon: <Icone sx={{ mr: 1, color: colorPrimary }} icone="Email" />,
      error: STR.errorUsername,
    },
    {
      name: "senha",
      label: STR.labelFieldPassword,
      type: viewPassword ? "text" : "password",
      placeholder: STR.placeholderPassword,
      error: STR.errorPassword,
      prefixIcon: <Icone sx={{ mr: 1, color: colorPrimary }} icone="VpnKey" />,
      suffixIcon: (
        <IconButton onClick={setViewPassword}>
          <Icone
            icone={viewPassword ? "Visibility" : "VisibilityOff"}
            sx={{ color: colorPrimary }}
          />
        </IconButton>
      ),
    },
  ];
  //
  return (
    <Grow in>
      <Paper sx={sxPaper}>
        <Stack gap={2}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Logo />
            <Body1
              sx={{
                fontSize: {
                  xs: "1rem",
                  md: "1.25rem",
                  lg: "2rem",
                },
                color: "black",
                fontFamily: "Arista",
              }}
            >
              {STR.labelCompany}
            </Body1>
          </Stack>
          <Divider />
          {schemaFormFields.map((fieldItem, idx) => (
            <Controller
              key={fieldItem.name}
              name={fieldItem.name}
              defaultValue=""
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  disabled={wait}
                  label={fieldItem.label}
                  placeholder={fieldItem.placeholder}
                  type={fieldItem.type}
                  variant="standard"
                  autoFocus={idx === 0}
                  InputProps={{
                    startAdornment: fieldItem.prefixIcon,
                    endAdornment: fieldItem.suffixIcon,
                  }}
                  InputLabelProps={{
                    color: "primary",
                    sx: {
                      color: "black",
                    },
                    shrink: true,
                  }}
                  inputProps={{
                    style: {
                      color: "black",
                    },
                  }}
                  helperText={
                    errors && errors[fieldItem.name] && fieldItem.error
                  }
                  error={errors && errors[fieldItem.name]}
                />
              )}
            />
          ))}

          <Button
            disabled={wait}
            startIcon={wait ? <CircularProgress size={20} /> : null}
            onClick={handleSubmit(onSubmit)}
            variant="contained"
          >
            {STR.labelBtnLogin}
          </Button>
        </Stack>
      </Paper>
    </Grow>
  );
};

export default Login;
